import { Badge, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@nexus/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { formatScriptCreatedAt } from "../../../lib/persona-script-meta.js";
import type { HubChromeScriptRow } from "./hub-chrome.types.js";

const TOOLTIP_DELAY_MS = 300;

/** A script or brief's send status. Hovering names the vendor, time and sender. */
export function OpportunityHubScriptRowSentBadge({
  row,
}: {
  row: Pick<HubChromeScriptRow, "title" | "sent" | "sentTo">;
}) {
  const { t, i18n } = useTranslation();
  const sentTo = row.sent ? row.sentTo : null;

  return (
    <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="slate"
            iconStart={row.sent ? "share" : "circle"}
            className="text-[11px]"
            data-testid="opportunity-hub-script-row-sent-badge"
          >
            {row.sent
              ? t("personaOpportunities.scripts.toolbar.sendStatusSent", "Sent to vendor")
              : t("personaOpportunities.scripts.toolbar.sendStatusNotSent", "Not sent")}
          </Badge>
        </TooltipTrigger>
        <TooltipContent align="end" className="max-w-80 px-3 py-2">
          <div className="flex flex-col gap-1.5" data-testid="opportunity-hub-script-row-sent-params">
            <p className="truncate font-medium">{row.title}</p>
            {sentTo ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                <SentParam label={t("personaOpportunities.hub.scripts.sentVendor", "Vendor")}>
                  {sentTo.vendorName}
                </SentParam>
                <SentParam label={t("personaOpportunities.hub.scripts.sentAt", "Sent")}>
                  {formatScriptCreatedAt(sentTo.sentAt, i18n.language)}
                </SentParam>
                {sentTo.sentByUserName ? (
                  <SentParam label={t("personaOpportunities.hub.scripts.sentBy", "By")}>
                    {sentTo.sentByUserName}
                  </SentParam>
                ) : null}
              </dl>
            ) : (
              <p className="opacity-70">
                {row.sent
                  ? t("personaOpportunities.hub.scripts.sentToVendor", "Sent to a vendor.")
                  : t("personaOpportunities.hub.scripts.notSentYet", "Not sent to a vendor yet.")}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function SentParam({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="opacity-70">{label}</dt>
      <dd className="tabular-nums">{children}</dd>
    </>
  );
}
